// hooks/useSegments.js - UPDATED with categoryFilters support
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getSegmentsFilterDefaults } from "../context/envDefaults";
import segmentsApiService from "../services/SegmentsApiService";
import {
	distanceFromRelevance,
	getRecommendedThreshold,
	relevanceFromDistance,
	resolveQueryThreshold,
} from "../utils";

// Default tier for segment search terms (sheet: Segments -> Strong Match).
const DEFAULT_SEGMENT_THRESHOLD = getRecommendedThreshold("SEGMENTS");
const DEFAULT_SEGMENT_RELEVANCE = Math.round(
	relevanceFromDistance(DEFAULT_SEGMENT_THRESHOLD, "SEGMENTS"),
);

export const useSegments = (initialFilters = {}, apiService = segmentsApiService) => {
	// ===== FILTER STATE =====
	// Internal nested structure for API
	const [internalFilters, setInternalFilters] = useState(() => {
		const defaultFilters = getSegmentsFilterDefaults();

		// Safe merge with initial filters
		const mergeFilters = (defaults, initial) => {
			if (!initial || typeof initial !== "object") return defaults;

			const merged = { ...defaults };

			Object.keys(initial).forEach((key) => {
				if (
					key === "segmentVector" ||
					key === "segmentMetrics" ||
					key === "demographicFilters" ||
					key === "cumulativeThresholds"
				) {
					merged[key] = { ...defaults[key] };
					if (initial[key] && typeof initial[key] === "object") {
						Object.keys(initial[key]).forEach((nestedKey) => {
							const initialValue = initial[key][nestedKey];
							const defaultValue = defaults[key][nestedKey];

							if (Array.isArray(defaultValue)) {
								merged[key][nestedKey] = Array.isArray(initialValue)
									? [...initialValue]
									: [];
							} else {
								merged[key][nestedKey] =
									initialValue !== undefined ? initialValue : defaultValue;
							}
						});
					}
				} else {
					merged[key] =
						initial[key] !== undefined ? initial[key] : defaults[key];
				}
			});

			return merged;
		};

		return mergeFilters(defaultFilters, initialFilters);
	});

	// Flattened filters for UI components (backwards compatibility)
	const filters = useMemo(() => {
		return {
			// Flatten segmentVector fields to root level for UI compatibility
			...internalFilters.segmentVector,
			// Keep segmentMetrics nested but also flatten common ones
			...internalFilters.segmentMetrics,
			// Include other top-level properties
			sortBy: internalFilters.sortBy,
			view: internalFilters.view,
			startIndex: internalFilters.startIndex,
			noOfResult: internalFilters.noOfResult,
			noOfDays: internalFilters.noOfDays,
			isAudienceRequire: internalFilters.isAudienceRequire,
			// Keep nested structure for API
			segmentVector: internalFilters.segmentVector,
			segmentMetrics: internalFilters.segmentMetrics,
			demographicFilters: internalFilters.demographicFilters,
			cumulativeThresholds: internalFilters.cumulativeThresholds,
			// NEW: Add categoryFilters to flattened structure
			categoryFilters: internalFilters.categoryFilters || [],
			demographicAge: internalFilters.demographicFilters?.ageRange || {},
			demographicGender: internalFilters.demographicFilters?.gender || null,
			demographicLocation:
				internalFilters.demographicFilters?.location &&
				internalFilters.demographicFilters?.locationType
					? {
							type: internalFilters.demographicFilters.locationType,
							selections: internalFilters.demographicFilters.location,
						}
					: {
							type: null,
							selections: [],
						},
		};
	}, [internalFilters]);

	// ===== SEARCH STATE =====
	const [searchQuery, setSearchQuery] = useState("");
	const [searchTerms, setSearchTerms] = useState([]);
	const [logicChain, setLogicChain] = useState([]);
	const [isSearching, setIsSearching] = useState(false);
	const [searchResults, setSearchResults] = useState(null);
	const [error, setError] = useState(null);

	// Computed search terms that include category filters for UI display
	const computedSearchTerms = useMemo(() => {
		const baseTerms = [...searchTerms]; // existing search terms from user input

		// Add category filters as visual search terms
		if (
			Array.isArray(internalFilters.categoryFilters) &&
			internalFilters.categoryFilters.length > 0
		) {
			internalFilters.categoryFilters.forEach((filter, index) => {
				if (filter.categoryL2) {
					const alreadyExists = baseTerms.some(
						(term) =>
							term.text.toLowerCase() === filter.categoryL2.toLowerCase(),
					);
					if (!alreadyExists) {
						baseTerms.push({
							id: `categoryFilter_${index}_${Date.now()}`,
							text: filter.categoryL2,
							score: 75,
							type: "include",
							source: "categoryFilter",
							isReadOnly: true,
						});
					}
				}
			});
		}

		return baseTerms;
	}, [searchTerms, internalFilters.categoryFilters]);

	// ===== UI STATE =====
	const [expandedSections, setExpandedSections] = useState({
		audienceType: false,
		categoryL1: false,
		categoryL2: false,
		demographics: false,
		performance: false,
		source: false,
		advanced: false,
	});

	// Filter options state with default fallback values
	const [filterOptions, setFilterOptions] = useState({
		audienceType: ["Consumer", "Business", "Mixed"],
		categoryL1: [
			"Demographic",
			"Interest",
			"Behavioral",
			"Geographic",
			"Lookalike",
		],
		categoryL2: [
			"Lifestage",
			"Shopping",
			"Automotive",
			"Finance",
			"Travel",
			"Entertainment",
		],
		attributeNames: ["Age", "Gender", "Income", "Location", "Interest"],
		sources: ["experian", "nielsen", "comscore", "custom"],
		segmentTypes: ["demographic", "behavioral", "geographic", "custom"],
		countries: ["US", "CA", "UK", "AU", "DE", "FR", "Global"],
		providers: ["openaudience", "experian", "nielsen"],
	});

	const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(false);
	const [hasFilterError, setHasFilterError] = useState(false);
	const [filterError, setFilterError] = useState(null);

	// ===== REFS =====
	const abortControllerRef = useRef(null);
	const lastUpdateRef = useRef(null);
	const filterOptionsAbortControllerRef = useRef(null);

	// ===== AUTHENTICATION CHECK FUNCTION =====
	const checkAuthentication = useCallback(() => {
		try {
			console.log("🔍 Checking authentication before search...");

			// Check if segmentsApiService is available
			if (!segmentsApiService) {
				console.error("❌ segmentsApiService not available");
				setError("Service unavailable. Please refresh the page.");
				return false;
			}

			// Use the service's authentication check
			const isAuth = apiService.isAuthenticated();

			if (!isAuth) {
				console.log("❌ User not authenticated, redirecting to login...");
				setError("Authentication required. Redirecting to login...");

				// Clear any existing search state
				setSearchResults(null);
				setSearchTerms([]);
				setSearchQuery("");

				// Use the service's redirect method
				apiService.redirectToLogin();
				return false;
			}

			console.log("✅ User is authenticated, proceeding with search");
			return true;
		} catch (error) {
			console.error("❌ Authentication check failed:", error);
			setError("Authentication check failed. Please refresh the page.");
			return false;
		}
	}, []);

	// ===== FILTER OPTIONS FETCHING FUNCTION =====
	const isLoadingFilterOptionsRef = useRef(false);
	const fetchFilterOptions = useCallback(
		async (forceRefresh = false) => {
			try {
				console.log("🔧 Fetching filter options from API...", { forceRefresh });

				// Don't fetch if already loading
				if (isLoadingFilterOptionsRef.current && !forceRefresh) {
					console.log("⏳ Filter options already loading, skipping...");
					return;
				}

				// Cancel previous request if still running
				if (filterOptionsAbortControllerRef.current) {
					filterOptionsAbortControllerRef.current.abort();
				}

				filterOptionsAbortControllerRef.current = new AbortController();
				isLoadingFilterOptionsRef.current = true;
				setIsLoadingFilterOptions(true);
				setHasFilterError(false);
				setFilterError(null);

				const response = await apiService.getFilterOptions(
					filterOptionsAbortControllerRef.current.signal,
				);

				if (response.success && response.data) {
					console.log("✅ Filter options fetched successfully:", {
						dataKeys: Object.keys(response.data),
						isCached: response.cached,
						isMock: response.isMock,
					});

					// Transform API response to match UI expectations
					const transformedOptions = {
						// Map common fields, handling different possible field names
						audienceType: response.data.audienceType ||
							response.data.audience_type || ["Consumer", "Business", "Mixed"],
						categoryL1: response.data.categoryL1 ||
							response.data.category_l1 ||
							response.data.categoryL_1 || [
								"Demographic",
								"Interest",
								"Behavioral",
								"Geographic",
								"Lookalike",
							],
						categoryL2: response.data.categoryL2 ||
							response.data.category_l2 ||
							response.data.categoryL_2 || [
								"Lifestage",
								"Shopping",
								"Automotive",
								"Finance",
								"Travel",
								"Entertainment",
							],
						attributeNames: response.data.attributeNames ||
							response.data.attribute_names ||
							response.data.attributeName || [
								"Age",
								"Gender",
								"Income",
								"Location",
								"Interest",
								"Education",
							],
						sources: response.data.sources ||
							response.data.source || [
								"experian",
								"nielsen",
								"comscore",
								"custom",
								"openaudience",
							],
						segmentTypes: response.data.segmentTypes ||
							response.data.segment_types ||
							response.data.type || [
								"demographic",
								"behavioral",
								"geographic",
								"interest",
								"lookalike",
							],
						segmentSources: response.data.segmentSources ||
							response.data.segment_sources ||
							response.data.segmentSource || [
								"Third Party Data",
								"First Party Data",
								"Lookalike",
								"Custom",
							],
						countries: response.data.countries ||
							response.data.allowedCountries ||
							response.data.allowed_countries || [
								"US",
								"CA",
								"UK",
								"AU",
								"DE",
								"FR",
								"JP",
								"IN",
								"BR",
								"Global",
							],
						providers: response.data.providers ||
							response.data.dataProvider ||
							response.data.data_provider || [
								"openaudience",
								"experian",
								"nielsen",
								"comscore",
								"custom",
							],
						accountNames: response.data.accountNames ||
							response.data.account_names ||
							response.data.accountName || [
								"OpenAudience",
								"AI Digital Org",
								"Custom Account",
							],
						status: response.data.status ||
							response.data.status || [
								"ready",
								"processing",
								"archived",
								"error",
							],

						// Add any additional fields from the API response
						...response.data,
					};

					setFilterOptions(transformedOptions);

					if (response.isMock) {
						console.log(
							"⚠️ Using mock filter options due to API error:",
							response.mockReason,
						);
					}
				} else {
					console.error("❌ Failed to fetch filter options:", response.error);
					setHasFilterError(true);
					setFilterError(response.error || "Failed to load filter options");
				}
			} catch (error) {
				if (error.name === "AbortError") {
					console.log("🛑 Filter options fetch aborted");
					return;
				}

				console.error("❌ Filter options fetch error:", error);
				setHasFilterError(true);
				setFilterError(error.message || "Failed to load filter options");
			} finally {
				isLoadingFilterOptionsRef.current = false;
				setIsLoadingFilterOptions(false);
				filterOptionsAbortControllerRef.current = null;
			}
		},
		[],
	);

	// ===== FETCH FILTER OPTIONS ON MOUNT =====
	useEffect(() => {
		console.log("🚀 useSegments hook mounted, fetching filter options...");
		fetchFilterOptions();

		// Cleanup function
		return () => {
			if (filterOptionsAbortControllerRef.current) {
				filterOptionsAbortControllerRef.current.abort();
			}
		};
	}, []); // Empty dependency array - only run on mount

	// ===== FILTER MANAGEMENT FUNCTIONS =====
	const updateFilter = useCallback((key, value) => {
		console.log("🔧 updateFilter called:", { key, value });

		setInternalFilters((prev) => {
			const newFilters = JSON.parse(JSON.stringify(prev));

			// Handle different key formats
			if (key.includes(".")) {
				// Nested key like 'segmentVector.audienceType'
				const keys = key.split(".");
				let current = newFilters;

				for (let i = 0; i < keys.length - 1; i++) {
					if (!current[keys[i]]) {
						current[keys[i]] = {};
					}
					current = current[keys[i]];
				}

				current[keys[keys.length - 1]] = value;
			} else {
				// Flat key - need to determine where it belongs
				if (
					[
						"audienceType",
						"categoryL1",
						"categoryL2",
						"attributeName",
						"attributeValue",
						"valueQualifier",
						"accountName",
						"type",
						"subType",
						"category",
						"subCategory",
						"source",
						"providerId",
						"isAbm",
						"isLeaf",
						"isNavOnly",
						"sellable",
						"isDeletedInSource",
						"allowedCountries",
					].includes(key)
				) {
					// segmentVector field
					newFilters.segmentVector[key] = value;
				} else if (
					[
						"reach",
						"avgEcpm",
						"requests",
						"weightedImpressions",
						"uniqueUsers",
						"advertiserSpend",
						"status",
						"weightedBidCpm",
					].includes(key)
				) {
					// segmentMetrics field
					newFilters.segmentMetrics[key] = value;
				} else if (
					[
						"demographicAge",
						"demographicGender",
						"demographicLocation",
					].includes(key)
				) {
					// Handle demographic filters
					if (!newFilters.demographicFilters) {
						newFilters.demographicFilters = {};
					}

					// Map to the correct demographic filter structure
					if (key === "demographicAge") {
						newFilters.demographicFilters.ageRange = value;
					} else if (key === "demographicGender") {
						newFilters.demographicFilters.gender = value;
					} else if (key === "demographicLocation") {
						if (
							value &&
							typeof value === "object" &&
							value.type &&
							Array.isArray(value.selections)
						) {
							// Convert from component format { type, selections } to API format
							newFilters.demographicFilters.locationType = value.type;
							newFilters.demographicFilters.location = value.selections;
						} else {
							// Fallback for other formats
							newFilters.demographicFilters.location = Array.isArray(value)
								? value
								: [];
							newFilters.demographicFilters.locationType = null;
						}
					}
				} else if (key === "cumulativeThresholds") {
					console.log("🔧 Updating cumulative thresholds:", value);
					if (!newFilters.cumulativeThresholds) {
						newFilters.cumulativeThresholds = {
							reach_relevance: 38000000,
							uniqueUsers: 1500000000,
							weightedImpressions: 700000,
							reach_composite: 38000000,
						};
					}
					newFilters.cumulativeThresholds = {
						...newFilters.cumulativeThresholds,
						...value,
					};
					console.log(
						"🔧 New cumulative thresholds:",
						newFilters.cumulativeThresholds,
					);

					// Also store at top level for UI compatibility
					newFilters[key] = value;
				} else if (key === "categoryFilters") {
					// NEW: Handle categoryFilters as a top-level array
					console.log("🔧 Updating category filters:", value);
					newFilters.categoryFilters = Array.isArray(value) ? value : [];
					console.log("🔧 New category filters:", newFilters.categoryFilters);
				} else {
					// Top-level field
					newFilters[key] = value;
				}
			}

			console.log("🔧 Filter updated:", { key, value, success: true });
			lastUpdateRef.current = {
				key,
				value,
				timestamp: new Date().toISOString(),
			};
			return newFilters;
		});
	}, []);

	const toggleArrayFilter = useCallback((key, value) => {
		console.log("🔧 toggleArrayFilter called:", { key, value });

		setInternalFilters((prev) => {
			const newFilters = JSON.parse(JSON.stringify(prev));

			// Determine the correct path for the array
			let arrayPath = [];
			let arrayKey = key;

			if (key.includes(".")) {
				arrayPath = key.split(".");
				arrayKey = arrayPath[arrayPath.length - 1];
			} else {
				// Determine the correct section for flat keys
				if (
					[
						"audienceType",
						"categoryL1",
						"categoryL2",
						"attributeName",
						"attributeValue",
						"valueQualifier",
						"accountName",
						"type",
						"subType",
						"category",
						"subCategory",
						"source",
						"providerId",
						"allowedCountries",
					].includes(key)
				) {
					arrayPath = ["segmentVector", key];
				} else if (
					["segmentName", "segmentExternalId", "status"].includes(key)
				) {
					arrayPath = ["segmentMetrics", key];
				} else {
					arrayPath = [key];
				}
			}

			// Navigate to the array
			let current = newFilters;
			for (let i = 0; i < arrayPath.length - 1; i++) {
				if (!current[arrayPath[i]]) {
					current[arrayPath[i]] = {};
				}
				current = current[arrayPath[i]];
			}

			const finalKey = arrayPath[arrayPath.length - 1];
			const currentArray = current[finalKey];

			if (!Array.isArray(currentArray)) {
				current[finalKey] = [];
			}

			const oldArray = Array.isArray(current[finalKey])
				? [...current[finalKey]]
				: [];
			const valueExists = oldArray.includes(value);

			if (valueExists) {
				current[finalKey] = oldArray.filter((item) => item !== value);
				console.log("🔧 Removed from array:", {
					key,
					value,
					newLength: current[finalKey].length,
				});
			} else {
				current[finalKey] = [...oldArray, value];
				console.log("🔧 Added to array:", {
					key,
					value,
					newLength: current[finalKey].length,
				});
			}

			lastUpdateRef.current = {
				key,
				value,
				timestamp: new Date().toISOString(),
			};
			return newFilters;
		});
	}, []);

	const clearAllFilters = useCallback(() => {
		console.log("🔧 clearAllFilters called");
		setInternalFilters(getSegmentsFilterDefaults());
	}, []);

	const toggleSection = useCallback((section) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	}, []);

	// ===== SEARCH TERM MANAGEMENT =====
	const onSearchInputChange = useCallback((value) => {
		setSearchQuery(value);
		setError(null);
	}, []);

	const reverseRelevanceToScore = useCallback(
		(relevance) => distanceFromRelevance(relevance, "SEGMENTS"),
		[],
	);

	const addSearchTerm = useCallback((termType, text, score, threshold) => {
		if (!text || !text.trim()) return;

		const newTerm = {
			id: Date.now(),
			text: text.trim(),
			score: score ?? DEFAULT_SEGMENT_RELEVANCE,
			threshold: threshold ?? DEFAULT_SEGMENT_THRESHOLD,
			type: termType,
		};

		setSearchTerms((prev) => [...prev, newTerm]);

		// Clear input after adding term
		setSearchQuery("");
	}, []);

	const removeSearchTerm = useCallback((termId) => {
		setSearchTerms((prev) => prev.filter((term) => term.id !== termId));
	}, []);

	const onLogicChainChange = useCallback((newLogicChain) => {
		setLogicChain(newLogicChain);
	}, []);

	// ===== SEARCH EXECUTION =====
	const transformSearchResults = useCallback((responseData) => {
		console.log("🔄 Transforming search results:", responseData);

		if (!responseData) return {};

		if (typeof responseData === "object" && !Array.isArray(responseData)) {
			const hasCorrectStructure = Object.values(responseData).some(
				(value) =>
					value && typeof value === "object" && Array.isArray(value.data),
			);

			if (hasCorrectStructure) {
				return responseData;
			}
		}

		if (Array.isArray(responseData)) {
			return {
				results: {
					data: responseData,
					totalCount: responseData.length,
				},
			};
		}

		if (responseData && typeof responseData === "object") {
			const transformed = {};
			let hasValidData = false;

			Object.entries(responseData).forEach(([key, value]) => {
				if (["totalCount", "totalRecords", "status", "message"].includes(key)) {
					return;
				}

				if (Array.isArray(value) && value.length > 0) {
					transformed[key] = {
						data: value,
						totalCount: value.length,
					};
					hasValidData = true;
				}
			});

			if (hasValidData) {
				return transformed;
			}
		}

		return {};
	}, []);

	const performSearch = useCallback(
		async (searchData = null, options = {}) => {
			try {
				// 🔍 AUTHENTICATION CHECK FIRST
				if (!checkAuthentication()) {
					console.log("🔍 Authentication failed, aborting search");
					return;
				}

				if (abortControllerRef.current) {
					abortControllerRef.current.abort();
				}

				abortControllerRef.current = new AbortController();
				setIsSearching(true);
				setError(null);

				console.log("🔍 Starting search with current state:", {
					searchQuery,
					searchTermsCount: searchTerms.length,
					logicChainLength: logicChain.length,
					currentFilters: {
						audienceType: filters.segmentVector?.audienceType?.length || 0,
						categoryL1: filters.segmentVector?.categoryL1?.length || 0,
						source: filters.segmentVector?.source?.length || 0,
						reach: filters.segmentMetrics?.reach || 0,
						avgEcpm: filters.segmentMetrics?.avgEcpm || 0,
						sortBy: filters.sortBy,
						noOfResult: filters.noOfResult,
						categoryFilters: filters.categoryFilters?.length || 0, // NEW: Log category filters
					},
				});

				// Build search terms
				const finalSearchTerms = [];
				const finalExcludeTerms = [];

				// Include terms from searchTerms state
				const includeTerms = searchTerms.filter(
					(term) => term.type === "include",
				);
				const excludeTerms = searchTerms.filter(
					(term) => term.type === "exclude",
				);

				includeTerms.forEach((term) => {
					finalSearchTerms.push({
						text: term.text,
						score: resolveQueryThreshold(term, "SEGMENTS"),
						type: "include",
						id: term.id,
						originalScore: term.score,
					});
				});

				excludeTerms.forEach((term) => {
					finalExcludeTerms.push({
						text: term.text,
						score: resolveQueryThreshold(term, "SEGMENTS"),
						type: "exclude",
						id: term.id,
						originalScore: term.score,
					});
				});

				// Include current search query if not already in terms
				if (searchQuery && searchQuery.trim()) {
					const queryText = searchQuery.trim();
					const alreadyExists = searchTerms.some(
						(term) => term.text === queryText,
					);
					if (!alreadyExists) {
						finalSearchTerms.push({
							text: queryText,
							score: DEFAULT_SEGMENT_THRESHOLD,
							type: "include",
							id: `query_${Date.now()}`,
							originalScore: DEFAULT_SEGMENT_RELEVANCE,
						});
					}
				}

				// Handle search data parameter if provided
				if (searchData) {
					if (Array.isArray(searchData)) {
						searchData.forEach((term) => {
							if (typeof term === "object" && term.text) {
								finalSearchTerms.push({
									text: term.text,
									score: term.score || 75,
									type: term.type || "include",
								});
							} else if (typeof term === "string") {
								finalSearchTerms.push({
									text: term,
									score: 75,
									type: "include",
								});
							}
						});
					} else if (typeof searchData === "string" && searchData.trim()) {
						finalSearchTerms.push({
							text: searchData.trim(),
							score: 75,
							type: "include",
						});
					}
				}

				console.log("🔍 Final search terms prepared:", {
					searchTermsCount: finalSearchTerms.length,
					excludeTermsCount: finalExcludeTerms.length,
					searchTerms: finalSearchTerms,
					excludeTerms: finalExcludeTerms,
				});

				// Call API with current filters
				const response = await apiService.performSegmentSearch(
					finalSearchTerms,
					finalExcludeTerms,
					internalFilters, // Pass internal nested filters to API
					{
						...options,
						signal: abortControllerRef.current.signal,
						logicChain: logicChain,
					},
				);

				if (response.success) {
					console.log("✅ Search completed successfully");

					let processedData = response.data;
					if (response.data?.body) {
						processedData = response.data.body;
					}

					const categorizedData = transformSearchResults(processedData);
					const allSegments = [];
					Object.values(categorizedData).forEach((category) => {
						if (category?.data && Array.isArray(category.data)) {
							allSegments.push(...category.data);
						}
					});

					setSearchResults({
						success: true,
						data: categorizedData,
						categories: categorizedData,
						segments: allSegments,
						totalCount: allSegments.length,
						totalRecords: allSegments.length,
						searchContext: {
							searchTerms: finalSearchTerms,
							excludeTerms: finalExcludeTerms,
							filters: internalFilters, // Use internal filters for context
							logicChain: logicChain,
							timestamp: new Date().toISOString(),
						},
					});
				} else {
					console.error("❌ Search failed:", response.error);
					setError(response.error || "Search failed. Please try again.");
					setSearchResults({ success: false, error: response.error });
				}
			} catch (error) {
				if (error.name === "AbortError") {
					console.log("🛑 Search aborted");
					return;
				}

				console.error("❌ Search Error:", error);
				const errorMessage =
					error.message || "Search failed. Please try again.";
				setError(errorMessage);
				setSearchResults({ success: false, error: errorMessage });
			} finally {
				setIsSearching(false);
				abortControllerRef.current = null;
			}
		},
		[
			searchQuery,
			searchTerms,
			logicChain,
			filters,
			reverseRelevanceToScore,
			transformSearchResults,
			checkAuthentication,
		],
	);

	// ===== COMPUTED VALUES =====
	const activeFilterCount = useMemo(() => {
		let count = 0;

		try {
			const audienceTypeCount = Array.isArray(
				internalFilters.segmentVector?.audienceType,
			)
				? internalFilters.segmentVector.audienceType.length
				: 0;
			const categoryL1Count = Array.isArray(
				internalFilters.segmentVector?.categoryL1,
			)
				? internalFilters.segmentVector.categoryL1.length
				: 0;
			const categoryL2Count = Array.isArray(
				internalFilters.segmentVector?.categoryL2,
			)
				? internalFilters.segmentVector.categoryL2.length
				: 0;
			const sourceCount = Array.isArray(internalFilters.segmentVector?.source)
				? internalFilters.segmentVector.source.length
				: 0;

			count +=
				audienceTypeCount + categoryL1Count + categoryL2Count + sourceCount;

			if (
				internalFilters.demographicFilters?.ageRange?.start !== 19 ||
				internalFilters.demographicFilters?.ageRange?.end !== 99
			)
				count++;
			if (
				Array.isArray(internalFilters.demographicFilters?.location) &&
				internalFilters.demographicFilters.location.length > 0
			)
				count++;
			if (internalFilters.demographicFilters?.gender) count++;

			const reach =
				typeof internalFilters.segmentMetrics?.reach === "number"
					? internalFilters.segmentMetrics.reach
					: 0;
			const avgEcpm =
				typeof internalFilters.segmentMetrics?.avgEcpm === "number"
					? internalFilters.segmentMetrics.avgEcpm
					: 0;

			if (reach > 0) count++;
			if (avgEcpm > 0) count++;

			// NEW: Count category filters
			if (
				Array.isArray(internalFilters.categoryFilters) &&
				internalFilters.categoryFilters.length > 0
			) {
				count += internalFilters.categoryFilters.length;
			}

			return count;
		} catch (error) {
			console.error("❌ Error calculating activeFilterCount:", error);
			return 0;
		}
	}, [
		internalFilters.segmentVector?.audienceType,
		internalFilters.segmentVector?.categoryL1,
		internalFilters.segmentVector?.categoryL2,
		internalFilters.segmentVector?.source,
		internalFilters.demographicFilters,
		internalFilters.segmentMetrics?.reach,
		internalFilters.segmentMetrics?.avgEcpm,
		internalFilters.categoryFilters, // NEW: Add to dependencies
	]);

	const hasActiveFilters = useMemo(
		() => activeFilterCount > 0,
		[activeFilterCount],
	);

	const filterSummary = useMemo(() => {
		const summary = [];

		try {
			if (
				Array.isArray(internalFilters.segmentVector?.audienceType) &&
				internalFilters.segmentVector.audienceType.length > 0
			) {
				summary.push(
					`Audience: ${internalFilters.segmentVector.audienceType.join(", ")}`,
				);
			}

			if (
				Array.isArray(internalFilters.segmentVector?.categoryL1) &&
				internalFilters.segmentVector.categoryL1.length > 0
			) {
				summary.push(
					`Categories: ${internalFilters.segmentVector.categoryL1.join(", ")}`,
				);
			}

			if (
				Array.isArray(internalFilters.segmentVector?.source) &&
				internalFilters.segmentVector.source.length > 0
			) {
				summary.push(
					`Sources: ${internalFilters.segmentVector.source.join(", ")}`,
				);
			}

			const reach =
				typeof internalFilters.segmentMetrics?.reach === "number"
					? internalFilters.segmentMetrics.reach
					: 0;
			if (reach > 0) {
				summary.push(`Min Reach: ${reach.toLocaleString()}`);
			}

			const avgEcpm =
				typeof internalFilters.segmentMetrics?.avgEcpm === "number"
					? internalFilters.segmentMetrics.avgEcpm
					: 0;
			if (avgEcpm > 0) {
				summary.push(`Min eCPM: ${avgEcpm.toFixed(2)}`);
			}

			// NEW: Add category filters to summary
			if (
				Array.isArray(internalFilters.categoryFilters) &&
				internalFilters.categoryFilters.length > 0
			) {
				const validFilters = internalFilters.categoryFilters.filter(
					(f) => f.categoryL2,
				);
				if (validFilters.length > 0) {
					summary.push(
						`Category Filters: ${validFilters.map((f) => f.categoryL2).join(", ")}`,
					);
				}
			}

			return summary;
		} catch (error) {
			console.error("❌ Error calculating filterSummary:", error);
			return [];
		}
	}, [
		internalFilters.segmentVector?.audienceType,
		internalFilters.segmentVector?.categoryL1,
		internalFilters.segmentVector?.source,
		internalFilters.segmentMetrics?.reach,
		internalFilters.segmentMetrics?.avgEcpm,
		internalFilters.categoryFilters, // NEW: Add to dependencies
	]);

	const validation = useMemo(() => {
		const errors = [];

		try {
			if (internalFilters.demographicFilters?.ageRange) {
				const { start, end } = internalFilters.demographicFilters.ageRange;
				if (typeof start === "number" && (start < 0 || start > 1000)) {
					errors.push("Age start must be between 0 and 1000");
				}
				if (typeof end === "number" && (end < 0 || end > 1000)) {
					errors.push("Age end must be between 0 and 1000");
				}
				if (
					typeof start === "number" &&
					typeof end === "number" &&
					start >= end
				) {
					errors.push("Age start must be less than age end");
				}
			}

			const reach =
				typeof internalFilters.segmentMetrics?.reach === "number"
					? internalFilters.segmentMetrics.reach
					: 0;
			const avgEcpm =
				typeof internalFilters.segmentMetrics?.avgEcpm === "number"
					? internalFilters.segmentMetrics.avgEcpm
					: 0;

			if (reach < 0) errors.push("Reach must be non-negative");
			if (avgEcpm < 0) errors.push("eCPM must be non-negative");

			const noOfResult =
				typeof internalFilters.noOfResult === "number"
					? internalFilters.noOfResult
					: 250;

			// NEW: Validate category filters
			if (Array.isArray(internalFilters.categoryFilters)) {
				internalFilters.categoryFilters.forEach((filter, index) => {
					if (
						filter.numericMin !== null &&
						filter.numericMax !== null &&
						filter.numericMin >= filter.numericMax
					) {
						errors.push(
							`Category Filter ${index + 1}: Min value must be less than max value`,
						);
					}
				});
			}

			return {
				isValid: errors.length === 0,
				errors,
			};
		} catch (error) {
			console.error("❌ Error in validation:", error);
			return { isValid: false, errors: ["Validation error occurred"] };
		}
	}, [
		internalFilters.demographicFilters?.ageRange,
		internalFilters.segmentMetrics?.reach,
		internalFilters.segmentMetrics?.avgEcpm,
		internalFilters.noOfResult,
		internalFilters.categoryFilters, // NEW: Add to dependencies
	]);

	const getSearchSummary = useCallback(() => {
		if (!searchResults?.segments) return null;

		const segments = searchResults.segments;
		const totalReach = segments.reduce(
			(sum, segment) =>
				sum + (segment.segmentMetrics?.uniqueUsers || segment.uniqueUsers || 0),
			0,
		);
		const avgEcpm =
			segments.length > 0
				? segments.reduce(
						(sum, segment) =>
							sum + (segment.segmentMetrics?.avgEcpm || segment.avgEcpm || 0),
						0,
					) / segments.length
				: 0;

		return {
			totalSegments: segments.length,
			totalReach,
			avgEcpm,
			searchQuery: searchQuery,
			searchTerms: searchTerms,
			appliedFilters: internalFilters,
			filterSnapshot: {
				audienceType: internalFilters.segmentVector?.audienceType || [],
				categoryL1: internalFilters.segmentVector?.categoryL1 || [],
				source: internalFilters.segmentVector?.source || [],
				reach: internalFilters.segmentMetrics?.reach || 0,
				avgEcpm: internalFilters.segmentMetrics?.avgEcpm || 0,
				categoryFilters: internalFilters.categoryFilters || [], // NEW: Include in snapshot
			},
		};
	}, [searchResults, searchQuery, searchTerms, internalFilters]);

	// ===== UTILITY FUNCTIONS =====
	const retryFetchFilterOptions = useCallback(() => {
		console.log("🔄 Retrying filter options fetch...");
		fetchFilterOptions(true); // Force refresh
	}, [fetchFilterOptions]);

	const resetSearch = useCallback(() => {
		setSearchQuery("");
		setSearchTerms([]);
		setLogicChain([]);
		setSearchResults(null);
		setError(null);
		setIsSearching(false);

		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
	}, []);

	// ===== COMPUTED STATE =====
	const hasResults = Boolean(
		searchResults?.success && searchResults?.segments?.length > 0,
	);
	const hasError = Boolean(error || (searchResults && !searchResults.success));
	const resultsCount = searchResults?.segments?.length || 0;

	// ===== CLEANUP =====
	useEffect(() => {
		return () => {
			if (abortControllerRef.current) {
				abortControllerRef.current.abort();
			}
			if (filterOptionsAbortControllerRef.current) {
				filterOptionsAbortControllerRef.current.abort();
			}
		};
	}, []);

	// Hydration useEffect with corrected version:
	useEffect(() => {
		if (
			window.segmentLLMResultsLoaded &&
			Array.isArray(window.segmentLLMResultsData)
		) {
			console.log(
				"🔄 Hydrating useSegments with stored LLM results:",
				window.segmentLLMResultsData.length,
			);

			// Get the display query from multiple sources
			const displayQuery =
				window.segmentLLMOriginalQuery ||
				window.segmentLLMDisplayQuery ||
				"sports and spanish";

			console.log("🎯 Setting search query and terms:", displayQuery);

			// Set both query and search terms properly
			setSearchQuery(displayQuery);
			setSearchTerms([
				{
					id: Date.now(),
					text: displayQuery,
					type: "include",
					score: 75,
				},
			]);

			// Set the results
			setSearchResults({
				success: true,
				data: window.segmentLLMResultsData,
				categories: [],
				segments: window.segmentLLMResultsData,
				totalCount:
					window.segmentLLMTotalItems || window.segmentLLMResultsData.length,
				totalRecords:
					window.segmentLLMTotalItems || window.segmentLLMResultsData.length,
				timestamp: Date.now(),
				metadata: { hydratedFrom: "window.segmentLLMResultsData" },
			});

			// Reset so it doesn't reapply
			window.segmentLLMResultsLoaded = false;

			console.log("✅ Segments hook hydrated successfully");
		}
	}, []);

	// ===== DEBUG LOGGING =====
	useEffect(() => {
		console.log("🔧 useSegments state updated:", {
			searchQuery,
			searchTermsCount: searchTerms.length,
			isSearching,
			hasResults,
			hasError,
			resultsCount,
			activeFilterCount,
			hasActiveFilters,
			isLoadingFilterOptions,
			hasFilterError,
			filterOptionsKeys: Object.keys(filterOptions),
			currentFilters: {
				audienceType: internalFilters.segmentVector?.audienceType?.length || 0,
				categoryL1: internalFilters.segmentVector?.categoryL1?.length || 0,
				source: internalFilters.segmentVector?.source?.length || 0,
				reach: internalFilters.segmentMetrics?.reach || 0,
				avgEcpm: internalFilters.segmentMetrics?.avgEcpm || 0,
				sortBy: internalFilters.sortBy,
				noOfResult: internalFilters.noOfResult,
				categoryFilters: internalFilters.categoryFilters?.length || 0, // NEW: Log category filters
			},
		});
	}, [
		searchQuery,
		searchTerms,
		isSearching,
		hasResults,
		hasError,
		resultsCount,
		activeFilterCount,
		hasActiveFilters,
		isLoadingFilterOptions,
		hasFilterError,
		filterOptions,
		internalFilters,
	]);

	const injectSearchResults = useCallback(
		(resultsData) => {
			console.log("Injecting search results directly:", resultsData);

			if (resultsData && resultsData.success) {
				setSearchResults({
					success: true,
					data: resultsData.data,
					segments: resultsData.segments || resultsData.data,
					totalRecords: resultsData.totalRecords || 0,
					searchContext: {
						searchTerms: searchTerms,
						filters: internalFilters,
						isInjected: true,
						timestamp: new Date().toISOString(),
					},
				});
				setIsSearching(false);
				setError(null);
			}
		},
		[searchTerms, internalFilters],
	);

	const searchWithOverride = useCallback(
		(searchTermsData, overrideResults) => {
			console.log("Search with override results:", {
				searchTermsData,
				overrideResults,
			});

			// Set search terms first
			setSearchTerms(searchTermsData || []);

			// Then inject results
			if (overrideResults) {
				injectSearchResults(overrideResults);
			}
		},
		[injectSearchResults],
	);

	return {
		// Filter state and actions
		filters,
		updateFilter,
		toggleArrayFilter,
		clearAllFilters,

		// UI state
		expandedSections,
		toggleSection,
		filterOptions,
		isLoadingFilterOptions,
		hasFilterError,
		filterError,
		retryFetchFilterOptions,
		fetchFilterOptions, // Expose fetch function for manual refresh

		// Search state and actions
		searchQuery,
		onSearchInputChange: onSearchInputChange,
		searchTerms: computedSearchTerms,
		addSearchTerm,
		removeSearchTerm,
		logicChain,
		onLogicChainChange,
		onSearch: performSearch, // Main search function with authentication check
		isSearching,
		searchResults,
		error,
		resetSearch,

		// Computed values
		activeFilterCount,
		hasActiveFilters,
		filterSummary,
		validation,
		getSearchSummary,
		hasResults,
		hasError,
		resultsCount,

		// Helper functions
		reverseRelevanceToScore,

		// Authentication utilities
		checkAuthentication,

		injectSearchResults,
		searchWithOverride,
	};
};
